import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { trackSuccessfulBookingConversion } from '../utils/adsConversion';
import '../styles/ConfirmSuccess.css';

const ConfirmSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [bookingData, setBookingData] = useState(null);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Additional fallback scroll attempts — helps on mobile where UI chrome or
  // floating elements can steal focus and push the viewport down after mount.
  useEffect(() => {
    const resetScroll = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      if (document.activeElement && typeof document.activeElement.blur === 'function') {
        document.activeElement.blur();
      }
    };

    resetScroll();
    const t1 = setTimeout(resetScroll, 50);
    const t2 = setTimeout(resetScroll, 300);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  useEffect(() => {
    // Get booking data from location state (only present after a successful save navigate)
    if (location.state?.bookingData) {
      setBookingData(location.state.bookingData);
    }

    // Animate success message
    gsap.fromTo('.success-container', 
      { opacity: 0, scale: 0.9 },
      { opacity: 1, scale: 1, duration: 0.6, ease: 'back.out' }
    );

    gsap.fromTo('.success-checkmark', 
      { opacity: 0, scale: 0 },
      { opacity: 1, scale: 1, duration: 0.8, delay: 0.2, ease: 'elastic.out' }
    );

    gsap.fromTo('.success-content', 
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 0.8, delay: 0.4 }
    );
  }, [location]);

  useEffect(() => {
    const referenceId = bookingData?.referenceId || bookingData?.referenceNumber;
    if (!referenceId) return;
    trackSuccessfulBookingConversion(referenceId);
  }, [bookingData]);

  const handleBackHome = () => {
    navigate('/');
  };

  return (
    <div className="confirm-success-page">
      <div className="success-container">
        {/* Top success block */}
        <div className="success-checkmark">
          <div className="checkmark-circle">
            <svg className="checkmark-svg" viewBox="0 0 52 52">
              <circle className="checkmark-circle-stroke" cx="26" cy="26" r="25" fill="none" />
              <path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
            </svg>
          </div>
        </div>

        <div className="success-content">
          <h1 className="success-title">Booking Confirmed!</h1>
          <p className="success-sub">Thank you for choosing Easy Drop Taxi. Your booking has been received successfully.</p>

          {/* Reference box */}
          <div className="reference-box">
            <div className="ref-icon">📅</div>
            <div className="ref-text">Your Booking Reference ID</div>
            <div className="ref-number">{bookingData?.referenceId || bookingData?.referenceNumber || '—'}</div>
          </div>

          <p className="contact-note">Our team will contact you within 5–10 minutes to confirm your trip.</p>

          <div className="features-row">
            <div className="feature">
              <i className="fa fa-shield"></i>
              <div className="feature-label">Safe & Reliable Service</div>
            </div>
            <div className="feature">
              <i className="fa fa-headset"></i>
              <div className="feature-label">24/7 Customer Support</div>
            </div>
            <div className="feature">
              <i className="fa fa-thumbs-up"></i>
              <div className="feature-label">Thank You for Choosing Us!</div>
            </div>
          </div>

          <div className="action-buttons vertical">
            <a
              className="btn btn-ghost"
              href="/"
              onClick={(e) => {
                e.preventDefault();
                handleBackHome();
              }}
            >
              <i className="fa fa-home"></i> Back to Home
            </a>
            <a className="btn btn-outline" href="tel:+919629954544"><i className="fa fa-phone"></i> Call Us Now</a>
            <a className="btn btn-outline" href={`https://wa.me/${bookingData?.mobile || ''}`} target="_blank" rel="noreferrer"><i className="fa fa-whatsapp"></i> Chat on WhatsApp</a>
          </div>
        </div>
      </div>

      {/* Decorative Background Elements */}
      <div className="decoration decoration-left"></div>
      <div className="decoration decoration-right"></div>
    </div>
  );
};

export default ConfirmSuccess;
